/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/EmptyState.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/EmptyState.tsx
 * filename: EmptyState.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { clsx } from 'clsx';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { GuidanceMark } from '@/platform/design-system/components/icons/GuidanceMark';
import {
  StateIllustration,
  type StateIllustrationVariant,
} from '@/platform/design-system/components/icons/StateIllustration';
import { Button } from './Button';
import type { EmptySurfaceStateKind } from './SurfaceState.types';

export interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  eyebrow?: string;
  hint?: string;
  variant?: 'panel' | 'compact';
  tone?: 'neutral' | 'positive' | 'attention';
  illustration?: StateIllustrationVariant;
  stateKind?: EmptySurfaceStateKind;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  eyebrow,
  hint,
  variant = 'panel',
  tone = 'neutral',
  illustration,
  stateKind = 'verified-empty',
  className,
}: EmptyStateProps) {
  const compact = variant === 'compact';
  const toneClasses = {
    neutral:
      'border-[rgb(var(--color-border-primary)/0.72)] bg-[rgb(var(--color-bg-secondary)/0.7)] text-[rgb(var(--color-text-secondary))]',
    positive: 'border-emerald-500/24 bg-emerald-500/8 text-emerald-600 dark:text-emerald-300',
    attention: 'border-amber-500/24 bg-amber-500/8 text-amber-700 dark:text-amber-300',
  }[tone];
  const RenderIcon = Icon ?? GuidanceMark;

  return (
    <section
      className={clsx(
        'relative flex flex-col items-center justify-center overflow-hidden rounded-xl px-4 text-center',
        compact ? 'min-h-32 py-5' : 'min-h-52 py-10',
        className
      )}
      aria-label={title}
      data-state-kind={stateKind}
    >
      {illustration || !Icon ? (
        <StateIllustration
          variant={illustration ?? (tone === 'positive' ? 'complete' : 'verified-empty')}
          className={clsx(
            'empty-state-breathe motion-reduce:animate-none',
            compact ? 'h-12 w-12' : 'h-16 w-16',
            toneClasses
          )}
        />
      ) : (
        <div
          data-state-kind="verified-empty"
          className={clsx(
            'flex items-center justify-center border empty-state-breathe motion-reduce:animate-none',
            compact ? 'h-10 w-10 rounded-lg' : 'h-12 w-12 rounded-xl',
            toneClasses
          )}
        >
          <RenderIcon className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
        </div>
      )}
      {eyebrow && (
        <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.17em] text-[rgb(var(--color-text-tertiary))]">
          {eyebrow}
        </p>
      )}
      <h3
        className={clsx(
          'font-semibold tracking-tight text-[rgb(var(--color-text-primary))]',
          compact ? 'mt-2.5 text-sm' : 'mt-4 text-lg'
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={clsx(
            'max-w-md text-[rgb(var(--color-text-secondary))]',
            compact ? 'mt-1 text-xs leading-5' : 'mt-1.5 text-sm leading-6'
          )}
        >
          {description}
        </p>
      )}
      {hint && (
        <p className="mt-2 max-w-sm text-[11px] leading-5 text-[rgb(var(--color-text-tertiary))]">
          {hint}
        </p>
      )}
      {(action || secondaryAction) && (
        <div
          className={clsx(
            'flex flex-wrap items-center justify-center gap-2',
            compact ? 'mt-3' : 'mt-5'
          )}
        >
          {action && (
            <Button variant="primary" size="sm" onClick={action.onClick} className="min-h-11 gap-2">
              {action.label}
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={secondaryAction.onClick}
              className="min-h-11"
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
